import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import UpcomingSchedule from './UpcomingSchedule'
import ScheduleCoverageIndicator from '../shared/ScheduleCoverageIndicator'
import SubscriptionWidget from './SubscriptionWidget' // Phase 3 Integration
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'

const ANNOUNCEMENT_KEY = 'ecommerce_announcement_seen'

function Dashboard() {
  const { t } = useTranslation('dashboard')
  const {
    selectedAccountId,
    selectedAccount,
    isLoadingAccounts,
    hasAccounts,
  } = useAccount()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    approvalQueue: 0,
    scheduled: 0,
    currentlyPosted: 0,
    expired: 0,
  })
  const [scheduledStories, setScheduledStories] = useState([])
  const [scheduleLoading, setScheduleLoading] = useState(true)
  const [scheduleError, setScheduleError] = useState(null)
  const [brandSettings, setBrandSettings] = useState(null)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [announcementDismissed, setAnnouncementDismissed] = useState(
    () => localStorage.getItem(ANNOUNCEMENT_KEY) === 'true'
  )

  const loadDashboardData = useCallback(async (signal) => {
    if (!selectedAccountId) {
      setStats({
        approvalQueue: 0,
        scheduled: 0,
        currentlyPosted: 0,
        expired: 0,
      })
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const statsData = await api.getStoryStats(selectedAccountId, { signal })
      if (!signal?.aborted) {
        setStats(statsData)
      }
    } catch (err) {
      if (err.name !== 'AbortError' && !signal?.aborted) {
        console.error('Failed to load dashboard data:', err)
        setError(err.message || t('errors:dashboard.loadFailed'))
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false)
      }
    }
  }, [selectedAccountId, t])

  const loadScheduledStories = useCallback(async (signal) => {
    if (!selectedAccountId) {
      setScheduledStories([])
      setScheduleLoading(false)
      return
    }

    try {
      setScheduleLoading(true)
      setScheduleError(null)

      const response = await api.getStories({
        status: 'scheduled',
        limit: 12,
        instagram_account_id: selectedAccountId,
        signal
      })
      if (!signal?.aborted) {
        const stories = (response?.stories ?? []).slice().sort((a, b) => {
          const aDate = new Date(a.scheduled_for || a.scheduledFor || 0)
          const bDate = new Date(b.scheduled_for || b.scheduledFor || 0)
          return aDate - bDate
        })

        setScheduledStories(stories)
      }
    } catch (err) {
      if (err.name !== 'AbortError' && !signal?.aborted) {
        console.error('Failed to load scheduled stories:', err)
        setScheduleError(err.message || t('errors:dashboard.loadFailed'))
      }
    } finally {
      if (!signal?.aborted) {
        setScheduleLoading(false)
      }
    }
  }, [selectedAccountId, t])

  const loadBrandSettings = useCallback(async (signal) => {
    if (!selectedAccountId) {
      setBrandSettings(null)
      setSettingsLoading(false)
      return
    }

    try {
      setSettingsLoading(true)
      const settings = await api.getBrandSettings(selectedAccountId, { signal })
      if (!signal?.aborted) {
        setBrandSettings(settings)
      }
    } catch (err) {
      if (err.name !== 'AbortError' && !signal?.aborted) {
        console.error('Failed to load brand settings:', err)
        // Set default if not found
        setBrandSettings({ post_interval_hours: 24 })
      }
    } finally {
      if (!signal?.aborted) {
        setSettingsLoading(false)
      }
    }
  }, [selectedAccountId])

  // Reload data when account changes
  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      if (!isLoadingAccounts) {
        await Promise.all([
          loadDashboardData(abortController.signal),
          loadScheduledStories(abortController.signal),
          loadBrandSettings(abortController.signal)
        ]);
      }
    };

    loadData();

    return () => {
      abortController.abort();
    };
  }, [selectedAccountId, isLoadingAccounts, loadDashboardData, loadScheduledStories, loadBrandSettings])

  if (loading || isLoadingAccounts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (!hasAccounts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="max-w-lg rounded-lg border border-dashed border-gray-300 bg-white p-4 sm:p-6 md:p-8 text-center shadow-sm">
          <h2 className="text-2xl font-semibold text-gray-900">{t('empty.title')}</h2>
          <p className="mt-4 text-gray-600">
            {t('empty.message')}
          </p>
          <a
            href="/accounts"
            className="mt-6 inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            {t('empty.button')}
          </a>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <p className="font-medium">{t('error.title')}</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={loadDashboardData}
          className="mt-3 text-sm font-medium text-red-600 hover:text-red-800"
        >
          {t('error.button')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Title - Phase 3 Integration */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('overview.title')}</h1>
        <p className="mt-2 text-gray-600">
          {selectedAccount
            ? t('overview.subtitle', {
                username:
                  selectedAccount.username ||
                  selectedAccount.instagram_username ||
                  selectedAccount.instagramUsername ||
                  'instagram'
              })
            : t('overview.subtitleNoAccount')}
        </p>
      </div>

      {/* Ecommerce Announcement Banner */}
      {hasAccounts && !announcementDismissed && (
        <div className="relative rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white overflow-hidden">
          <button
            type="button"
            onClick={() => {
              localStorage.setItem(ANNOUNCEMENT_KEY, 'true')
              setAnnouncementDismissed(true)
            }}
            className="absolute top-3 right-3 text-white/70 hover:text-white"
            aria-label={t('announcement.dismiss', 'Dismiss')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 className="text-lg font-bold mb-2">
            {t('announcement.title', 'Neu: E-Commerce für deinen Shop')}
          </h3>
          <ul className="text-sm text-white/90 space-y-1 mb-4">
            <li>✦ {t('announcement.bullet1', 'Produkte aus deinem Shop importieren')}</li>
            <li>✦ {t('announcement.bullet2', 'KI erstellt automatisch Video-Stories')}</li>
            <li>✦ {t('announcement.bullet3', 'Freigeben, planen & posten')}</li>
          </ul>
          <Link
            to="/ecommerce"
            className="inline-flex items-center px-4 py-2 text-sm font-medium bg-white text-purple-700 rounded-lg hover:bg-white/90 transition-colors"
          >
            {t('announcement.cta', 'Jetzt entdecken')} &rarr;
          </Link>
        </div>
      )}

      {/* Subscription Widget - Full Width Banner */}
      <SubscriptionWidget />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1: Approval Queue */}
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.approval.label')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.approvalQueue}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Scheduled */}
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.scheduled.label')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.scheduled}</p>
            </div>
          </div>
        </div>

        {/* Card 3: Currently Posted */}
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.posted.label')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.currentlyPosted}</p>
            </div>
          </div>
        </div>

        {/* Card 4: Expired */}
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="p-3 bg-gray-100 rounded-lg">
                <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.expired.label')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Coverage Indicator */}
      <ScheduleCoverageIndicator
        scheduledCount={scheduledStories.length}
        postIntervalHours={brandSettings?.post_interval_hours || 24}
        loading={settingsLoading || scheduleLoading}
        variant="full"
        showCTA={true}
      />

      {/* Upcoming schedule */}
      <UpcomingSchedule
        stories={scheduledStories}
        loading={scheduleLoading}
        error={scheduleError}
        onRefresh={loadScheduledStories}
        instagramAccountId={selectedAccountId}
      />
    </div>
  )
}

export default Dashboard
